/**
 * ItemSalesReport.jsx — R7
 * Route: /pos/reports/item-sales
 * API:   GET /api/pos/reports/item-sales
 *
 * Item-wise qty, gross revenue, tax, net revenue.
 * Two tabs: Item-wise | Category Summary. Sort by qty/revenue/category.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Popover, Spin, Tabs } from "antd";
import { Download, Filter, Calendar } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base //.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const pillStyle   = (bg,color)=>({background:bg,color,fontWeight:800,fontSize:12,padding:"5px 10px",borderRadius:999,border:`1px solid ${color}33`,whiteSpace:"nowrap"});
const downloadCSV = (columns,data,filename)=>{
  const h=columns.filter(c=>c.dataIndex).map(c=>c.title).join(",");
  const r=data.map(row=>columns.filter(c=>c.dataIndex).map(c=>`"${String(row[c.dataIndex]??"").replace(/"/g,'""')}"`).join(","));
  const blob=new Blob([[h,...r].join("\n")],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
};

const ItemSalesReport = () => {
  const currency=getCurrency(); const apiURL=getApiUrl();
  const [dateRange,     setDateRange]     = useState({startDate:getBizDate(),endDate:getBizDate()});
  const [activeTab,     setActiveTab]     = useState("items");
  const [loading,       setLoading]       = useState(false);
  const [items,         setItems]         = useState([]);
  const [categoryBreak, setCategoryBreak] = useState([]);
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible,setPreviewVisible]= useState(false);
  const [exportData,    setExportData]    = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [filters,       setFilters]       = useState({
    order_type: null,
    is_complementary: {value:"false",label:"Paid sales only"},
    sort_by: {value:"revenue",label:"By Revenue"},
  });

  const orderTypeOptions = ["Table Service","Room Service","Delivery","Takeaway","Counter"].map(v=>({value:v,label:v}));
  const sortOptions      = [{value:"revenue",label:"By Revenue"},{value:"qty",label:"By Quantity"},{value:"category",label:"By Category"}];
  const compOptions      = [{value:"false",label:"Paid sales only"},{value:"true",label:"Complementary only"}];

  const fetchReport=useCallback(async()=>{
    setLoading(true);
    try{
      const params={
        start_date:dateRange.startDate.format("YYYY-MM-DD"),
        end_date:dateRange.endDate.format("YYYY-MM-DD"),
        sort_by:filters.sort_by?.value||"revenue",
        ...(filters.order_type&&{order_type:filters.order_type.value}),
        ...(filters.is_complementary&&{is_complementary:filters.is_complementary.value}),
      };
      const res=await axios.get(`${apiURL}/pos/item-sales`,{headers:{Authorization:`Bearer ${getToken()}`},params});
      if(res.data.success){setItems(res.data.items||[]);setCategoryBreak(res.data.categoryBreakdown||[]);}
    }catch(err){Swal.fire({icon:"error",title:"Error",text:err?.response?.data?.error||err.message});}
    finally{setLoading(false);}
  },[dateRange,filters,apiURL]);

  useEffect(()=>{fetchReport();},[fetchReport]);

  const itemColumns=[
    {title:"Item Name",               dataIndex:"item_name",      width:200,fixed:"left"},
    {title:"Code",                    dataIndex:"item_code",      width:90},
    {title:"Category",                dataIndex:"category_name",  width:130},
    {title:"Qty Sold",                dataIndex:"total_qty_sold", width:100,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Avg Price (${currency})`, dataIndex:"avg_unit_price", width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Gross (${currency})`,     dataIndex:"gross_revenue",  width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Tax (${currency})`,       dataIndex:"tax_amount",     width:100,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Net Revenue (${currency})`,dataIndex:"net_revenue",   width:140,align:"right",fixed:"right",
     render:v=><strong style={{color:"#166534"}}>{Number(v||0).toFixed(2)}</strong>},
    {title:"Orders",                  dataIndex:"order_count",    width:80,align:"right"},
  ];

  const catColumns=[
    {title:"Category",                       dataIndex:"category_name",  width:200,fixed:"left"},
    {title:"Item Variants",                  dataIndex:"item_variants",  width:120,align:"right"},
    {title:"Total Qty",                      dataIndex:"total_qty",      width:100,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Total Revenue (${currency})`,    dataIndex:"total_revenue",  width:160,align:"right",fixed:"right",
     render:v=><strong style={{color:"#166534"}}>{Number(v||0).toFixed(2)}</strong>},
  ];

  const currentCols = activeTab==="items" ? itemColumns : catColumns;
  const currentData = activeTab==="items" ? items : categoryBreak;
  const totalRevenue = items.reduce((s,r)=>s+Number(r.net_revenue||0),0);
  const totalQty     = items.reduce((s,r)=>s+Number(r.total_qty_sold||0),0);

  const filterContent=(
    <div style={{width:240,display:"grid",gap:12}}>
      {[
        {label:"Order Type",key:"order_type",options:orderTypeOptions},
        {label:"Sales Type",key:"is_complementary",options:compOptions},
        {label:"Sort By",   key:"sort_by",options:sortOptions},
      ].map(f=>(
        <div key={f.key}>
          <label style={{fontSize:12,fontWeight:700,display:"block",marginBottom:4}}>{f.label}</label>
          <SelectComponent options={f.options} isClearable={f.key!=="sort_by"} value={filters[f.key]}
            onChange={v=>setFilters(p=>({...p,[f.key]:v}))} styles={{control:b=>({...b,fontSize:13})}}/>
        </div>))}
      <Button type="primary" size="small" block onClick={()=>{setFilterVisible(false);fetchReport();}}>Apply Filters</Button>
    </div>
  );

  return(
    <div className="page-wrapper"><div className="content container-fluid">
      <div className="row align-items-center mb-3">
        <div className="col-auto d-flex align-items-center gap-3">
          <h2 style={{fontSize:20,fontWeight:700,borderBottom:"2px solid #007bff",paddingBottom:4,margin:0}}>Item-wise Sales Report</h2>
          <DateRangePicker
            key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
            initialSettings={{startDate:dateRange.startDate.toDate(),endDate:dateRange.endDate.toDate(),
              ranges:{Today:[getBizDate().clone().startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],
                "This Month":[getBizDate().clone().startOf("month").toDate(),getBizDate().clone().endOf("month").toDate()],
                "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(),getBizDate().clone().subtract(1,"month").endOf("month").toDate()]},
              locale:{format:"DD MMM YYYY",applyLabel:"Apply",cancelLabel:"Cancel"},alwaysShowCalendars:true,autoApply:false}}
            onApply={(_,p)=>setDateRange({startDate:p.startDate,endDate:p.endDate})}>
            <div className="input-group" style={{width:250}}>
              <input type="text" className="form-control text-center fw-bold" style={{height:38,fontSize:14,borderRight:"none",background:"#fff"}}
                value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly/>
              <span className="input-group-text" style={{background:"#fff",borderLeft:"none",height:38,cursor:"pointer"}}><Calendar size={15} color="#495057"/></span>
            </div>
          </DateRangePicker>
        </div>
        <div className="col-auto ms-auto d-flex align-items-center gap-2">
          <span style={pillStyle("#f0fdf4","#166534")}>{currency} {totalRevenue.toFixed(0)} Net Revenue</span>
          <span style={pillStyle("#eff6ff","#1d4ed8")}>{items.length} Items · {totalQty.toFixed(0)} Qty</span>
          <Popover content={filterContent} title="Filters" trigger="click"
            open={filterVisible} onOpenChange={setFilterVisible} placement="bottomRight">
            <Button type="default" className="d-flex align-items-center" style={{height:38}}>
              <Filter size={15} className="me-2"/>Filter &amp; Sort
            </Button>
          </Popover>
          <div className="dropdown">
            <Button type="primary" className="d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" style={{height:38}}>
              <Download size={15} className="me-2"/>Download
            </Button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={()=>downloadCSV(currentCols,currentData,`Item_Sales_${activeTab}_${dateRange.startDate.format("YYYYMMDD")}.csv`)}>Download CSV</button></li>
              <li><button className="dropdown-item" onClick={()=>{setExportData(currentData);setExportHeaders(currentCols.filter(c=>c.dataIndex).map(c=>({label:c.title,key:c.dataIndex})));setPreviewVisible(true);}}>Print / PDF Preview</button></li>
            </ul>
          </div>
        </div>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{marginBottom:12}}
        items={[{key:"items",label:`Item-wise (${items.length})`},{key:"category",label:`Category Summary (${categoryBreak.length})`}]}/>

      {loading?<div className="text-center my-5"><Spin size="large"/></div>:(
        <Table columns={currentCols} dataSource={currentData} rowKey={(_,i)=>i}
          scroll={{x:"max-content"}} size="small" bordered
          pagination={{pageSize:100,showTotal:t=>`${t} ${activeTab==="items"?"items":"categories"}`}}/>
      )}
      <PrintReportPreview visible={previewVisible} onClose={()=>setPreviewVisible(false)}
        reportTitle={`Item-wise Sales — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
        propertyName="" data={exportData} headers={exportHeaders} footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL}/>
    </div></div>
  );
};

export default ItemSalesReport;
